import { Popup } from 'react-map-gl/maplibre';
import { Separator } from 'shared-ui';

import { StatusPill } from '@/components/shared/StatusPill';
import {
    getLeakStatusPillPresentation,
    getPoiStatusPillPresentation,
} from '@/components/shared/statusPillPresentation';
import { formatPipeType, getLeakageRateDisplay, formatLeakageRateMlD } from '../utils/poiFeatureProperties';
import type { PoiFeatureProperties } from '../utils/poiFeatureProperties';
import { normaliseEntityType } from '../utils/entityStatus';
import { MARKER_ANCHORED_POPUP_OFFSET } from '../utils/markerDimensions';
import { HoverCardField } from './HoverCardField';
import { What3WordsField } from './What3WordsField';

interface PoiPopupProps {
    poi: PoiFeatureProperties;
    /** Geographic anchor — MapLibre projects + tracks this itself, so no screen-pixel math here. */
    longitude: number;
    latitude: number;
    /** Wired to the POI interactions so hovering onto the card cancels its dismiss timer. */
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
}

/**
 * POI hover card, positioned by MapLibre's own `<Popup>`: it projects the lng/lat, tracks it on
 * pan/zoom, picks a side that fits the viewport (`anchor` unset = auto), and clears the marker
 * via a per-anchor `offset`. Interactive (no `--passive`) so the cursor can travel onto the card
 * without it dismissing. Because MapLibre positions by the rendered DOM box, the card width can
 * be set in rem with no pixel math; the popup chrome is stripped via `.orbit-hover-popup`.
 */
export function PoiPopup({ poi, longitude, latitude, onMouseEnter, onMouseLeave }: PoiPopupProps) {
    const pipeType = formatPipeType(poi.pipe_type);
    // Both families share this card; the single-winner hit rule means a hover is only ever
    // one entity. A leak hover must never present as a "Point of Interest".
    const isLeak = normaliseEntityType(poi.entity_type) === 'leak';
    const heading = isLeak ? 'Find & Fix Leak' : 'Point of Interest';
    const pill = isLeak ? getLeakStatusPillPresentation(poi.status) : getPoiStatusPillPresentation(poi.status);
    const leakage = getLeakageRateDisplay(poi);

    return (
        <Popup
            longitude={longitude}
            latitude={latitude}
            closeButton={false}
            closeOnClick={false}
            closeOnMove={false}
            focusAfterOpen={false}
            offset={MARKER_ANCHORED_POPUP_OFFSET}
            maxWidth="none"
            className="orbit-hover-popup"
        >
            <div
                className="border-border/30 bg-card w-[20rem] rounded-md border p-5 shadow-lg"
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
            >
                <p className="text-foreground text-xl leading-tight font-black uppercase">{heading}</p>

                {/* The card never names the owning client (design decision) and stays
                    informational — actions live in the details panel. */}
                <div className="mt-3">
                    <StatusPill presentation={pill} />
                </div>

                <Separator className="my-5" />

                <div className="space-y-5">
                    {isLeak ? (
                        <HoverCardField
                            label="Leak What3words"
                            meta="(Actual)"
                            value={<What3WordsField value={poi.leak_what3words ?? poi.what3words} />}
                        />
                    ) : (
                        <HoverCardField
                            label="Point of Interest What3words"
                            meta="(Original)"
                            value={<What3WordsField value={poi.what3words} />}
                        />
                    )}

                    <HoverCardField label="Pipe Type" value={pipeType ?? '—'} />

                    <HoverCardField
                        label="Leakage Rate"
                        meta={`(${leakage.provenance})`}
                        value={<span>Ml/D&nbsp;&nbsp;{formatLeakageRateMlD(leakage.value)}</span>}
                    />
                </div>
            </div>
        </Popup>
    );
}
